import { LogLevel } from '@datocms/cma-client-browser';
import { CompositionControlsGenerator } from '@src/entrypoints/ControlGenerator/CompositionControlsGenerateService';
import { MEDIA_QUERY_FRAGMENT } from '@src/gql/media-gql';
import { AIPromptRepository } from '@src/repositories/AIPromptRepository';
import { createDatoClient, createGQLClient } from '@src/utility/dato';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { Button, Canvas, SidebarPanel } from 'datocms-react-ui';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ControlRow } from './ControlRow';
import { MergeLabel } from './MergeLabel';

import type { AITextConfigPatch } from './AITextControlSelector';
import type {
  ControlConfiguration,
  DefaultBrandKitValue,
  QuoteControlType,
} from '@src/components/sidepanel/CompositionSettings/types';
import type {
  CompositionControlGroupDato,
  GraphicsPromptOption,
} from '@src/entrypoints/ControlGenerator/CompositionControlsGenerateService';
import type { DatoShowcase } from '@src/types.ts/story';
import type { RenderFieldExtensionCtx } from 'datocms-plugin-sdk';

/** Only Graphics prompts drive an AI text control (PLAT-10912 DS1). */
const GRAPHICS_PROMPT_TYPE = 'Graphics';

const logger = LogManager.getLogger('entrypoint:ControlGenerator');

type PropTypes = {
  ctx: RenderFieldExtensionCtx;
};

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function ControlGenerator({ ctx }: PropTypes) {
  const [generatedControlGroups, setGeneratedControlGroups] = useState<
    CompositionControlGroupDato[]
  >([]);
  const [mediaRefetch, setMediaRefetch] = useState(false);
  //  const controlsContext = JSON.parse(
  //  (ctx.item?.attributes?.test_source as string) || '[]',
  // );
  const sourceContext = JSON.parse(
    (ctx.item?.attributes?.source as string) || '{}',
  );
  const controlsContext = JSON.parse(
    (ctx.item?.attributes?.controls as string) || '[]',
  );
  const [valueErrors, setValueErrors] = useState<{ [key: string]: string }>({});
  const [selectedMergeFieldProperty, setSelectedMergeFieldProperty] = useState<
    { groupIndex: number; controlIndex: number; property: string }[]
  >([]);

  const [imageOptions, setImageOptions] = useState<
    { id: string; label: string; url: string }[]
  >([]);
  const [logoOptions, setLogoOptions] = useState<
    { id: string; label: string; url: string }[]
  >([]);
  /**
   * `null` means "not known" — still loading, or the load failed. The two are
   * told apart by `promptsLoadFailed`, because only one of them is worth
   * alarming the author about.
   */
  const [graphicsPrompts, setGraphicsPrompts] = useState<
    GraphicsPromptOption[] | null
  >(null);
  const [promptsLoadFailed, setPromptsLoadFailed] = useState(false);

  const resetTrigger = useRef(false);

  const dClient = createDatoClient({
    apiToken: ctx.currentUserAccessToken || '',
    environment: ctx.environment,
    logLevel: LogLevel.NONE,
  });

  const handleClick = async () => {
    const generateControlsService = new CompositionControlsGenerator();
    const generted =
      generateControlsService.generateControlsFromCreatomate(sourceContext);
    setGeneratedControlGroups(generted);
    // insertControlsIntoDato(generted);
  };

  const updateControlLabel = (
    groupIndex: number,
    controlIndex: number,
    newLabel: string,
  ) => {
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) =>
                  j === controlIndex
                    ? { ...control, label: newLabel }
                    : control,
              ),
            }
          : controlGroup,
      ),
    );
  };

  const updateControlValue = (
    groupIndex: number,
    controlIndex: number,
    newValue:
      | {
          value?: string | number;
          brandKit?: DefaultBrandKitValue;
          quote?: QuoteControlType;
        }
      | undefined,
  ) => {
    setValueErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[`${groupIndex}-${controlIndex}`];
      return newErrors;
    });
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) =>
                  j === controlIndex
                    ? { ...control, default: newValue }
                    : control,
              ),
            }
          : controlGroup,
      ),
    );
  };

  const updateControlType = (
    groupIndex: number,
    controlIndex: number,
    newValue: 'image' | 'logo' | 'headshot' | 'text' | 'aiText',
  ) => {
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) => {
                  if (j !== controlIndex) return control;
                  // An aiText control needs an id (its generated options are
                  // keyed by it) and a base-text default, so seed both on the
                  // switch rather than leaving the author to notice.
                  if (newValue === 'aiText') {
                    return {
                      defaultBaseTextScope: 'selection',
                      ...control,
                      type: newValue,
                      id: control.id || slugify(control.label),
                    } as ControlConfiguration;
                  }
                  return { ...control, type: newValue } as ControlConfiguration;
                },
              ),
            }
          : controlGroup,
      ),
    );
  };

  const updateControlId = (
    groupIndex: number,
    controlIndex: number,
    id: string,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control => ({
      ...control,
      id,
    }));
  };

  const updateAITextConfig = (
    groupIndex: number,
    controlIndex: number,
    patch: AITextConfigPatch,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control =>
      control.type === 'aiText' ? { ...control, ...patch } : control,
    );
  };

  const updateSpeakerControl = (
    groupIndex: number,
    controlIndex: number,
    textFieldId: string,
    fieldType: 'title' | 'name' | 'headshot',
  ) => {
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) => {
                  if (j === controlIndex && control.type === 'speaker') {
                    // Update controlBindings: remove old binding for this fieldType and add new one
                    const newBindings = { ...control.controlBindings };

                    // Remove any existing binding for this fieldType
                    Object.keys(newBindings).forEach(key => {
                      if (newBindings[key] === fieldType) {
                        delete newBindings[key];
                      }
                    });

                    // Add new binding
                    newBindings[textFieldId] = fieldType;

                    return { ...control, controlBindings: newBindings };
                  }
                  return control;
                },
              ),
            }
          : controlGroup,
      ),
    );
  };

  const toggleControlEnabled = (groupIndex: number, controlIndex: number) => {
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) =>
                  j === controlIndex
                    ? { ...control, enabled: !control.enabled }
                    : control,
              ),
            }
          : controlGroup,
      ),
    );
  };

  const updateControlAtIndex = (
    groupIndex: number,
    controlIndex: number,
    updater: (control: ControlConfiguration) => ControlConfiguration,
  ) => {
    setGeneratedControlGroups(prevControls =>
      prevControls.map((controlGroup, i) =>
        i === groupIndex
          ? {
              ...controlGroup,
              controls: controlGroup.controls.map(
                (control: ControlConfiguration, j) =>
                  j === controlIndex ? updater(control) : control,
              ),
            }
          : controlGroup,
      ),
    );
  };

  const toggleAllowTranslation = (
    groupIndex: number,
    controlIndex: number,
    checked: boolean,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control => ({
      ...control,
      allowTranslation: checked,
      id: control.id || slugify(control.label),
    }));
  };

  const toggleAllowQuickEdit = (
    groupIndex: number,
    controlIndex: number,
    checked: boolean,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control => ({
      ...control,
      allowQuickEdit: checked,
      id: control.id || slugify(control.label),
    }));
  };

  const updatePresetTranslation = (
    groupIndex: number,
    controlIndex: number,
    languageCode: string,
    value: string,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control => ({
      ...control,
      translations: { ...(control.translations ?? {}), [languageCode]: value },
    }));
  };

  const removePresetTranslation = (
    groupIndex: number,
    controlIndex: number,
    languageCode: string,
  ) => {
    updateControlAtIndex(groupIndex, controlIndex, control => {
      const next = { ...(control.translations ?? {}) };
      delete next[languageCode];
      return { ...control, translations: next };
    });
  };

  const setValueError = (
    groupIndex: number,
    controlIndex: number,
    error: string,
  ) => {
    setValueErrors(prevErrors => ({
      ...prevErrors,
      [`${groupIndex}-${controlIndex}`]: error,
    }));
  };

  const insertControlsIntoDato = async (
    controlGroups: CompositionControlGroupDato[],
  ) => {
    // Update the field in DatoCMS
    if (Object.keys(valueErrors).length > 0) {
      ctx.alert('Please fix the value errors before saving.');
      return;
    }
    if (ctx.item?.id) {
      const { controls: enabledControls, errors } =
        new CompositionControlsGenerator().prepareControlsForSave(
          controlGroups,
          graphicsPrompts,
        );

      if (errors.length > 0) {
        ctx.alert(errors.join(' '));
        return;
      }

      try {
        await dClient.items.update(ctx.item.id, {
          controls: JSON.stringify(enabledControls),
        });
        ctx.notice('Controls generated and saved successfully!');
      } catch (error) {
        console.error('Error updating test_source:', error);
        ctx.alert('Failed to save generated controls');
      }
    }
  };

  useEffect(() => {
    if (controlsContext.length > 0) {
      setGeneratedControlGroups(() => {
        // Group controls by all element paths combined
        const controlsByElement = new Map<string, ControlConfiguration[]>();

        controlsContext.forEach((control: ControlConfiguration) => {
          // Get all element names from all paths
          const elementNames = control.elementPaths.map(elementPath => {
            const path = elementPath as number[];
            const element = path.reduce((acc, curr) => {
              return acc?.elements?.[curr];
            }, sourceContext);
            return element?.name;
          });

          const pathKey = elementNames.join(', ') || control.label;

          if (!controlsByElement.has(pathKey)) {
            controlsByElement.set(pathKey, []);
          }
          controlsByElement.get(pathKey)!.push(control);
        });

        // Convert grouped controls to the expected format
        return Array.from(controlsByElement.entries()).map(
          ([fieldName, controls]) => {
            return {
              fieldName,
              controls: controls.map(
                (control: ControlConfiguration & { enabled?: boolean }) => ({
                  ...control,
                  enabled: control?.enabled ?? true,
                }),
              ),
            };
          },
        );
      });
    }
  }, [
    resetTrigger.current,
    ctx.item?.attributes?.source,
    ctx.item?.attributes?.controls,
  ]);

  const getShowcase = async (showcaseId: string) => {
    const gqlClient = createGQLClient({
      includeDrafts: true,
      excludeInvalid: false,
      environment: ctx.environment,
      authToken: ctx.currentUserAccessToken || '',
    });
    const query = `query getAlbum($id: ItemId) {
      showcase(filter: {id: {eq: $id}}) {
        id
        title
        organizationArtifacts ${MEDIA_QUERY_FRAGMENT}
        organizationLogos ${MEDIA_QUERY_FRAGMENT}
      }
    }`;

    const data = (await gqlClient.request(query, {
      id: showcaseId,
    })) as { showcase: DatoShowcase };

    if (data?.showcase?.organizationArtifacts) {
      setImageOptions(
        (data.showcase.organizationArtifacts as any[]).map((image: any) => ({
          id: image.id,
          label: image.title,
          url: image.url,
        })),
      );
    }
    if (data?.showcase?.organizationLogos) {
      setLogoOptions(
        (data.showcase.organizationLogos as any[]).map((logo: any) => ({
          id: logo.id,
          label: logo.title,
          url: logo.url,
        })),
      );
    }
  };

  useEffect(() => {
    if (ctx.item?.attributes?.created_by_showcase) {
      getShowcase(ctx.item?.attributes?.created_by_showcase as string);
    }
  }, [ctx.item?.attributes?.created_by_showcase, mediaRefetch]);

  // Graphics prompts for the AI text control's dropdown. No by-type query —
  // there are a handful of records, so the existing repository fetch is filtered
  // client-side (PLAT-10912 DS3).
  //
  // A failed fetch stays `null`, NOT an empty list: an empty list means "this
  // environment has no Graphics prompt" (an authoring error worth blocking the
  // save over), while `null` means "we don't know" — which must neither claim
  // there are none nor invalidate an already-configured control. The failure
  // is flagged separately so the in-flight `null` doesn't render as an error.
  useEffect(() => {
    const repository = new AIPromptRepository(
      createGQLClient({
        includeDrafts: true,
        environment: ctx.environment,
        authToken: ctx.currentUserAccessToken || '',
      }),
    );
    repository
      .getAllPrompts()
      .then(prompts => {
        setGraphicsPrompts(
          prompts
            .filter(prompt => prompt.promptType === GRAPHICS_PROMPT_TYPE)
            .map(prompt => ({ id: prompt.id, title: prompt.title })),
        );
        setPromptsLoadFailed(false);
      })
      .catch(error => {
        logger.error('Failed to load Graphics AI prompts', {
          environment: ctx.environment,
          ...extractErrorInfo(error),
        });
        setGraphicsPrompts(null);
        setPromptsLoadFailed(true);
      });
  }, [ctx.environment, ctx.currentUserAccessToken]);

  const handleSelectMergeField = (
    groupIndex: number,
    controlIndex: number,
    property: string,
    checked: boolean,
  ) => {
    if (checked) {
      setSelectedMergeFieldProperty(prevSelectedMergeFieldProperty => [
        ...prevSelectedMergeFieldProperty,
        { groupIndex, controlIndex, property },
      ]);
    } else {
      setSelectedMergeFieldProperty(prevSelectedMergeFieldProperty =>
        prevSelectedMergeFieldProperty.filter(
          item =>
            item.groupIndex !== groupIndex ||
            item.controlIndex !== controlIndex ||
            item.property !== property,
        ),
      );
    }
  };

  const handleMergeControls = () => {
    const generateControlsService = new CompositionControlsGenerator();
    const mergedControls = generateControlsService.mergeControls(
      generatedControlGroups,
      selectedMergeFieldProperty,
    );
    setGeneratedControlGroups(mergedControls);
    setSelectedMergeFieldProperty([]);
  };

  const handleSplitField = (groupIndex: number, controlIndex: number) => {
    const generateControlsService = new CompositionControlsGenerator();
    const splitControls = generateControlsService.splitControlsByElementPaths(
      sourceContext,
      generatedControlGroups,
      groupIndex,
      controlIndex,
    );
    setGeneratedControlGroups(splitControls);
  };

  const handleReset = () => {
    setSelectedMergeFieldProperty([]);
    setValueErrors({} as { [key: string]: string });
    resetTrigger.current = !resetTrigger.current;
  };

  const speakerOptions = generatedControlGroups
    .flatMap(controlGroup =>
      controlGroup.controls.filter(control => control.type === 'text'),
    )
    .map(control => ({
      id: control.id || '',
      label: control.label,
    }));

  const speakerHeadshotOptions = generatedControlGroups
    .flatMap(controlGroup =>
      controlGroup.controls.filter(
        control => control.type === 'image' || control.type === 'headshot',
      ),
    )
    .map(control => ({
      id: control.id || '',
      label: control.label,
    }));

  return (
    <Canvas ctx={ctx}>
      <SidebarPanel title="Composition Controls" noPadding={true}>
        <Container>
          <div
            style={{
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {generatedControlGroups.map((controlGroup, groupIndex) => (
              <div key={controlGroup.fieldName}>
                <h3>{controlGroup.fieldName}</h3>
                {controlGroup.controls.map(
                  (
                    control: ControlConfiguration & { enabled?: boolean },
                    controlIndex: number,
                  ) => (
                    <ControlRow
                      ctx={ctx}
                      key={control.label}
                      control={control}
                      onToggleEnabled={() =>
                        toggleControlEnabled(groupIndex, controlIndex)
                      }
                      onUpdateLabel={value =>
                        updateControlLabel(groupIndex, controlIndex, value)
                      }
                      onUpdateCustomValue={value =>
                        updateControlValue(groupIndex, controlIndex, value)
                      }
                      onUpdateControlType={value =>
                        updateControlType(groupIndex, controlIndex, value)
                      }
                      onUpdateControlId={id =>
                        updateControlId(groupIndex, controlIndex, id)
                      }
                      onUpdateAITextConfig={patch =>
                        updateAITextConfig(groupIndex, controlIndex, patch)
                      }
                      graphicsPrompts={graphicsPrompts}
                      promptsLoadFailed={promptsLoadFailed}
                      setValueError={error =>
                        setValueError(groupIndex, controlIndex, error)
                      }
                      onUpdateSpeakerControl={(id, type) =>
                        updateSpeakerControl(groupIndex, controlIndex, id, type)
                      }
                      onSelectMergeField={(property, checked) =>
                        handleSelectMergeField(
                          groupIndex,
                          controlIndex,
                          property,
                          checked,
                        )
                      }
                      selectedMergeFieldProperty={
                        selectedMergeFieldProperty?.[0]?.property || null
                      }
                      speakerTextFieldsOptions={speakerOptions}
                      speakerHeadshotOptions={speakerHeadshotOptions}
                      imageOptions={imageOptions}
                      logoOptions={logoOptions}
                      mediaRefetch={() => setMediaRefetch(!mediaRefetch)}
                      onSplitField={() =>
                        handleSplitField(groupIndex, controlIndex)
                      }
                      onToggleAllowTranslation={checked =>
                        toggleAllowTranslation(
                          groupIndex,
                          controlIndex,
                          checked,
                        )
                      }
                      onToggleAllowQuickEdit={checked =>
                        toggleAllowQuickEdit(groupIndex, controlIndex, checked)
                      }
                      onUpdatePresetTranslation={(lang, value) =>
                        updatePresetTranslation(
                          groupIndex,
                          controlIndex,
                          lang,
                          value,
                        )
                      }
                      onRemovePresetTranslation={lang =>
                        removePresetTranslation(groupIndex, controlIndex, lang)
                      }
                    />
                  ),
                )}
              </div>
            ))}
          </div>
          <div
            style={{
              padding: 10,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button type="button" onClick={handleClick}>
              (Re)-Generate controls
            </Button>
            <div style={{ display: 'flex', gap: 16 }}>
              <Button type="button" buttonType="muted" onClick={handleReset}>
                Reset
              </Button>
              <Button
                type="button"
                buttonType="primary"
                onClick={() => insertControlsIntoDato(generatedControlGroups)}
              >
                Save controls
              </Button>
            </div>
          </div>
          {selectedMergeFieldProperty.length > 0 && (
            <MergeLabel
              selectedMergeFieldProperty={selectedMergeFieldProperty}
              onCancel={() => setSelectedMergeFieldProperty([])}
              onMerge={handleMergeControls}
            />
          )}
        </Container>
      </SidebarPanel>
    </Canvas>
  );
}

const Container = styled.div`
  background-color: var(--light-bg-color);
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 600px;
  overflow-y: auto;
  scrollbar-color: #cccccc #f5f5f5 !important;
  &::-webkit-scrollbar {
    width: 8px !important;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #cccccc !important;
    width: 8px !important;
  }
  &::-webkit-scrollbar-track {
    background-color: #f5f5f5 !important;
  }
`;
